import { useEffect, useMemo, useRef, useState } from "react";
import * as pdfjs from "pdfjs-dist";
/* Vite bundles this as a blob Worker — avoids .mjs MIME issues on some hosts */
import PdfJsWorker from "pdfjs-dist/build/pdf.worker.min.mjs?worker";

let pdfWorkerPort = null;
function ensurePdfWorker() {
  if (pdfWorkerPort) return;
  pdfWorkerPort = new PdfJsWorker();
  pdfjs.GlobalWorkerOptions.workerPort = pdfWorkerPort;
}

function isPdfFile({ fileName = "", fileType = "" } = {}) {
  const name = String(fileName || "").toLowerCase();
  const type = String(fileType || "").toLowerCase();
  if (type.startsWith("image/")) return false;
  return type.includes("pdf") || name.endsWith(".pdf");
}

/** Render first PDF page into a canvas that fits the pane (contain). */
function PdfFitCanvas({ fileUrl, title }) {
  const viewportRef = useRef(null);
  const canvasRef = useRef(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const viewportEl = viewportRef.current;
    const canvas = canvasRef.current;
    if (!viewportEl || !canvas || !fileUrl) return undefined;

    let cancelled = false;
    let pdfDoc = null;
    let renderTask = null;
    let painting = false;
    let needsRepaint = false;

    const paint = async () => {
      if (painting) {
        needsRepaint = true;
        return;
      }
      painting = true;
      needsRepaint = false;

      try {
        setError("");
        if (!pdfDoc) {
          ensurePdfWorker();
          pdfDoc = await pdfjs.getDocument({ url: fileUrl }).promise;
        }
        if (cancelled) return;

        const page = await pdfDoc.getPage(1);
        if (cancelled) return;

        const base = page.getViewport({ scale: 1 });
        const availW = Math.max(viewportEl.clientWidth, 1);
        const availH = Math.max(viewportEl.clientHeight, 1);
        const scale = Math.min(availW / base.width, availH / base.height);
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        const viewport = page.getViewport({ scale: scale * dpr });

        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        canvas.style.width = `${Math.floor(viewport.width / dpr)}px`;
        canvas.style.height = `${Math.floor(viewport.height / dpr)}px`;

        const ctx = canvas.getContext("2d", { alpha: false });
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        if (renderTask) {
          try {
            renderTask.cancel();
          } catch {
            /* ignore */
          }
        }

        renderTask = page.render({
          canvasContext: ctx,
          viewport,
          background: "rgb(255,255,255)",
        });
        await renderTask.promise;
      } catch (err) {
        if (cancelled || err?.name === "RenderingCancelledException") return;
        setError("Unable to preview this PDF.");
      } finally {
        painting = false;
        if (!cancelled && needsRepaint) {
          paint();
        }
      }
    };

    paint();

    const ro = new ResizeObserver(() => {
      paint();
    });
    ro.observe(viewportEl);

    return () => {
      cancelled = true;
      ro.disconnect();
      if (renderTask) {
        try {
          renderTask.cancel();
        } catch {
          /* ignore */
        }
      }
      if (pdfDoc) {
        try {
          pdfDoc.destroy();
        } catch {
          /* ignore */
        }
      }
    };
  }, [fileUrl]);

  return (
    <div
      ref={viewportRef}
      className="invoice-preview-pane__viewport invoice-preview-pane__viewport--fit"
      title={title}
    >
      {error ? (
        <p className="mb-0 text-muted invoice-preview-pane__error">{error}</p>
      ) : (
        <canvas ref={canvasRef} className="invoice-preview-pane__canvas" />
      )}
    </div>
  );
}

export default function InvoicePreviewPane({
  fileName = "",
  fileType = "",
  fileUrl = "",
}) {
  const pdf = useMemo(
    () => isPdfFile({ fileName, fileType }),
    [fileName, fileType]
  );

  if (!fileUrl) {
    return (
      <div className="invoice-preview-pane invoice-preview-pane--empty">
        <p className="mb-0 text-muted">No invoice preview available.</p>
      </div>
    );
  }

  return (
    <div className="invoice-preview-pane">
      {pdf ? (
        <PdfFitCanvas
          fileUrl={fileUrl}
          title={fileName || "Lovell Invoice"}
        />
      ) : (
        <div className="invoice-preview-pane__viewport invoice-preview-pane__viewport--fit">
          <img
            className="invoice-preview-pane__image"
            src={fileUrl}
            alt={fileName || "Uploaded invoice"}
          />
        </div>
      )}
    </div>
  );
}
